import type { FC } from "react";
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  addBreadCrumbs,
  currentRouteSelector,
  setCurrentRoute,
} from "@/features/currentRouteSlice";
import { getUrlFromPath } from "@/routerConfig";
import { shouldHandleOwnBreadcrumbs } from "@/routerConfig/breadcrumbConfig";
import { useAppDispatch, useAppSelector } from "@/storeHooks";
import type { Breadcrumb } from "@/types/view";
import type { IconName } from "../NavIcon";
import NavBtnContent from "./NavBtnContent";
import css from "./NavMenu.module.css";

type NavMenuItemProps = {
  path: string;
  icon?: IconName;
  isBeta?: boolean;
  isExternal?: boolean;
  children: string;
  breadCrumbHandledExternally?: boolean;
  parentLabel: string;
};

/**
 * Leaf level nav menu item
 */
const NavMenuItem: FC<NavMenuItemProps> = ({
  children,
  path,
  isExternal,
  icon,
  isBeta,
  breadCrumbHandledExternally,
  parentLabel,
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const href = getUrlFromPath(path);
  const currentRoute = useAppSelector(currentRouteSelector);
  const isCurrentRoute = currentRoute.startsWith(path);

  useEffect(() => {
    if (isCurrentRoute && breadCrumbHandledExternally !== true) {
      const breadCrumbs: Breadcrumb[] = [];
      if (parentLabel) {
        breadCrumbs.push({ label: parentLabel, path: undefined });
      }
      if (children) {
        breadCrumbs.push({ label: children, path: undefined });
      }
      if (!shouldHandleOwnBreadcrumbs(currentRoute)) {
        dispatch(addBreadCrumbs(breadCrumbs));
      }
    }
  }, [
    breadCrumbHandledExternally,
    children,
    currentRoute,
    dispatch,
    isCurrentRoute,
    parentLabel,
    path,
  ]);

  return (
    <li key={path}>
      <a
        href={href}
        aria-current={isCurrentRoute ? "page" : undefined}
        className={css.itemButton}
        onClick={(e) => {
          // Check if Command/Ctrl key is pressed
          if (e.metaKey || e.ctrlKey) {
            // Let the default behavior handle opening in new tab
            return;
          }

          // set the current route for breadcrumbs
          dispatch(setCurrentRoute(path));

          e.preventDefault();
          if (isExternal) {
            const fromPath = window.location.pathname;

            // force a reload if already on this route
            if (fromPath.startsWith(href)) {
              window.location.assign(href);
              return;
            }
          }
          navigate(path);
        }}
      >
        <NavBtnContent icon={icon} isBeta={isBeta}>
          {children}
        </NavBtnContent>
      </a>
    </li>
  );
};

export default NavMenuItem;
